import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const C = {
  navy:    '#0A1628', gold:    '#C9A84C', black:   '#0A0A0A',
  white:   '#FFFFFF', surface: '#F5F6F8', border:  '#D0D5DD',
  borderLt:'#E8EBF0', text:    '#0A0A0A', textSec: '#3D4552',
  muted:   '#6B7280', mutedLt: '#9CA3AF',
  success: '#0A7C42', danger:  '#C0222A',
};

const COLORS = ['#0D6E8A', '#C9A84C', '#0A7C42', '#6D28D9'];

const classColors: Record<string, { color: string; bg: string }> = {
  hedge_fund:     { color: '#0D6E8A', bg: '#EFF8FB' },
  pe:             { color: '#92640A', bg: '#FDF6E7' },
  vc:             { color: '#1D4ED8', bg: '#EFF4FF' },
  re:             { color: '#B91C1C', bg: '#FEF2F2' },
  credit:         { color: '#0F766E', bg: '#F0FDFA' },
  infrastructure: { color: '#6D28D9', bg: '#F5F3FF' },
};

const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const classLabels: Record<string, string> = {
  hedge_fund: 'Hedge Fund', pe: 'Private Equity', vc: 'Venture Capital',
  re: 'Real Estate', credit: 'Credit', infrastructure: 'Infrastructure',
};

interface Fund {
  id: string; name: string; asset_class: string; strategy: string;
  aum_min: number; aum_max: number; minimum_subscription: number;
  management_fee: number; performance_fee: number; lockup_months: number;
  redemption_period: string; vehicle_type: string; regional_focus: string;
  headquarters: string; inception_date: string; open_to_sma: boolean;
  firm: { name: string };
}
interface Performance { fund_id: string; year: number; month: number; return_pct: number; }

const card: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '4px', padding: '18px 20px', marginBottom: '12px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};

export default function Compare() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [funds, setFunds]             = useState<Fund[]>([]);
  const [performance, setPerformance] = useState<Performance[]>([]);
  const [loading, setLoading]         = useState(true);
  const [activeSection, setActiveSection] = useState<'performance' | 'terms' | 'overview'>('performance');

  const fundIds = (searchParams.get('funds') || '').split(',').filter(Boolean);

  useEffect(() => { if (fundIds.length > 0) fetchData(); else setLoading(false); }, []);

  const fetchData = async () => {
    const { data: fundsData } = await supabase.from('funds').select('*, firm:firms(name)').in('id', fundIds);
    if (fundsData) setFunds(fundsData);
    const { data: perfData } = await supabase.from('fund_performance').select('*').in('fund_id', fundIds).order('year', { ascending: true }).order('month', { ascending: true });
    if (perfData) setPerformance(perfData);
    setLoading(false);
  };

  const getStats = (fundId: string) => {
    const returns = performance.filter(p => p.fund_id === fundId).map(p => p.return_pct);
    if (returns.length === 0) return null;
    const mean = returns.reduce((a, b) => a + b, 0) / returns.length;
    const annReturn = mean * 12;
    const variance = returns.reduce((s, r) => s + Math.pow(r - mean, 2), 0) / (returns.length - 1);
    const vol = Math.sqrt(variance) * Math.sqrt(12);
    const sharpe = vol === 0 ? null : (annReturn - 5) / vol;
    let peak = 100, nav = 100, maxDD = 0;
    for (const r of returns) { nav = nav * (1 + r / 100); if (nav > peak) peak = nav; const dd = (nav - peak) / peak * 100; if (dd < maxDD) maxDD = dd; }
    const downside = returns.filter(r => r < 0);
    let sortino = null;
    if (downside.length > 0) { const dv = Math.sqrt(downside.reduce((s, r) => s + Math.pow(r, 2), 0) / downside.length) * Math.sqrt(12); sortino = dv === 0 ? null : (annReturn - 5) / dv; }
    return { annReturn: annReturn.toFixed(2), vol: vol.toFixed(2), sharpe: sharpe?.toFixed(2) ?? '-', sortino: sortino?.toFixed(2) ?? '-', best: Math.max(...returns).toFixed(2), worst: Math.min(...returns).toFixed(2), maxDD: maxDD.toFixed(2), months: returns.length };
  };

  const buildChartData = () => {
    const navByFund: Record<string, Record<string, number>> = {};
    const allDates = new Set<string>();
    funds.forEach(f => { navByFund[f.id] = {}; });
    funds.forEach(fund => {
      let nav = 100;
      performance.filter(p => p.fund_id === fund.id).sort((a, b) => a.year !== b.year ? a.year - b.year : a.month - b.month).forEach(p => {
        nav = nav * (1 + p.return_pct / 100);
        const label = `${p.year}-${String(p.month).padStart(2, '0')}`;
        allDates.add(label);
        navByFund[fund.id][label] = parseFloat(nav.toFixed(2));
      });
    });
    return Array.from(allDates).sort().map(dateKey => {
      const [year, month] = dateKey.split('-');
      const point: any = { label: `${monthNames[parseInt(month) - 1]} ${year}` };
      funds.forEach(f => { point[f.id] = navByFund[f.id][dateKey] ?? null; });
      return point;
    });
  };

  const chartData = buildChartData();

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px', padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '12px', fontFamily: 'system-ui' }}>
        <div style={{ color: C.muted, marginBottom: '6px', fontWeight: 600 }}>{label}</div>
        {payload.map((p: any) => (
          <div key={p.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '3px' }}>
            <span style={{ color: C.textSec }}>{funds.find(f => f.id === p.dataKey)?.name}</span>
            <span style={{ color: p.color, fontFamily: '"Courier New", monospace', fontWeight: 700 }}>{p.value?.toFixed(2)}</span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) return (
    <div style={{ padding: '32px', background: C.surface, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: C.muted, fontSize: '13px', fontFamily: 'system-ui' }}>Loading comparison...</div>
    </div>
  );

  if (funds.length === 0) return (
    <div style={{ padding: '32px', background: C.surface, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '10px', fontFamily: 'system-ui' }}>
      <div style={{ fontSize: '16px', fontWeight: 600, color: C.text, fontFamily: 'Georgia, serif' }}>No funds selected</div>
      <div style={{ fontSize: '13px', color: C.muted }}>Go to AltConnect and select up to 4 funds to compare</div>
      <button onClick={() => navigate('/alt-connect')} style={{ marginTop: '8px', background: C.navy, border: 'none', borderRadius: '3px', color: C.white, fontSize: '12.5px', fontWeight: 600, padding: '9px 20px', cursor: 'pointer', fontFamily: 'system-ui' }}>
        Go to AltConnect
      </button>
    </div>
  );

  const ColGrid = ({ children }: { children: React.ReactNode }) => (
    <div style={{ display: 'grid', gridTemplateColumns: `180px repeat(${funds.length}, 1fr)`, gap: '10px', marginBottom: '6px', alignItems: 'stretch' }}>
      {children}
    </div>
  );

  const DataCell = ({ value, color, mono }: { value: string; color?: string; mono?: boolean }) => (
    <div style={{ ...card, padding: '10px 14px', marginBottom: 0, fontSize: '13px', fontWeight: 600, color: color || C.text, fontFamily: mono ? '"Courier New", monospace' : 'system-ui', boxShadow: 'none' }}>
      {value}
    </div>
  );

  const RowLabel = ({ label }: { label: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', fontSize: '11px', fontWeight: 600, color: C.muted, letterSpacing: '0.3px', paddingLeft: '2px' }}>
      {label}
    </div>
  );

  return (
    <div style={{ padding: '28px 32px', background: C.surface, minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      <button onClick={() => navigate('/alt-connect')} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px', color: C.textSec, padding: '5px 12px', cursor: 'pointer', fontSize: '12px', marginBottom: '16px', fontFamily: 'system-ui', fontWeight: 500 }}>
        ← Back to AltConnect
      </button>

      <div style={{ marginBottom: '20px', paddingBottom: '18px', borderBottom: `1px solid ${C.border}` }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.black, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>Fund Comparison</h1>
        <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>Comparing {funds.length} fund{funds.length !== 1 ? 's' : ''} side by side</p>
      </div>

      {/* Fund headers */}
      <div style={{ display: 'grid', gridTemplateColumns: `180px repeat(${funds.length}, 1fr)`, gap: '10px', marginBottom: '16px' }}>
        <div />
        {funds.map((fund, i) => (
          <div key={fund.id} style={{ ...card, borderTop: `3px solid ${COLORS[i]}`, padding: '14px 16px', marginBottom: 0 }}>
            <div style={{ fontSize: '9px', fontWeight: 700, color: COLORS[i], textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '5px' }}>
              {classLabels[fund.asset_class] || fund.asset_class}
            </div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: C.navy, marginBottom: '2px', lineHeight: 1.3 }}>{fund.name}</div>
            <div style={{ fontSize: '11px', color: C.muted }}>{fund.firm?.name}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '16px', border: `1px solid ${C.border}`, borderRadius: '3px', width: 'fit-content', overflow: 'hidden' }}>
        {(['performance', 'terms', 'overview'] as const).map((s, i, arr) => (
          <button key={s} onClick={() => setActiveSection(s)} style={{
            padding: '7px 20px', fontSize: '12px', cursor: 'pointer', border: 'none',
            background: activeSection === s ? C.navy : C.white,
            color: activeSection === s ? C.white : C.textSec,
            fontFamily: 'system-ui', fontWeight: 600, textTransform: 'capitalize',
            borderRight: i < arr.length - 1 ? `1px solid ${C.border}` : 'none',
          }}>{s}</button>
        ))}
      </div>

      {/* Performance */}
      {activeSection === 'performance' && (
        <>
          {chartData.length > 0 && (
            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: C.navy }}>Cumulative Performance</div>
                  <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px' }}>Indexed to 100 at inception</div>
                </div>
                <div style={{ display: 'flex', gap: '16px' }}>
                  {funds.map((f, i) => (
                    <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ width: '16px', height: '2px', background: COLORS[i], display: 'inline-block' }} />
                      <span style={{ fontSize: '11px', color: C.muted }}>{f.name}</span>
                    </div>
                  ))}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.borderLt} />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.mutedLt }} tickLine={false} axisLine={{ stroke: C.border }} interval={Math.floor(chartData.length / 6)} />
                  <YAxis tick={{ fontSize: 10, fill: C.mutedLt }} tickLine={false} axisLine={false} width={36} domain={['auto', 'auto']} />
                  <Tooltip content={<CustomTooltip />} />
                  {funds.map((f, i) => (
                    <Line key={f.id} type="linear" dataKey={f.id} stroke={COLORS[i]} strokeWidth={1.8} dot={false} activeDot={{ r: 3 }} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div style={{ marginTop: '4px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: `180px repeat(${funds.length}, 1fr)`, gap: '10px', marginBottom: '6px' }}>
              <div />
              {funds.map((f, i) => (
                <div key={f.id} style={{ fontSize: '10px', fontWeight: 700, color: COLORS[i], letterSpacing: '0.4px', textTransform: 'uppercase', paddingLeft: '14px' }}>{f.name}</div>
              ))}
            </div>
            {[
              { label: 'Annualised Return',    key: 'annReturn', suffix: '%' },
              { label: 'Annualised Volatility', key: 'vol',       suffix: '%' },
              { label: 'Sharpe Ratio',          key: 'sharpe',    suffix: '' },
              { label: 'Sortino Ratio',         key: 'sortino',   suffix: '' },
              { label: 'Best Month',            key: 'best',      suffix: '%' },
              { label: 'Worst Month',           key: 'worst',     suffix: '%' },
              { label: 'Max Drawdown',          key: 'maxDD',     suffix: '%' },
              { label: 'Months of Data',        key: 'months',    suffix: '' },
            ].map(row => (
              <ColGrid key={row.key}>
                <RowLabel label={row.label} />
                {funds.map((fund, i) => {
                  const stats = getStats(fund.id);
                  const val = stats ? (stats as any)[row.key] : null;
                  const displayVal = val !== null && val !== '-' ? `${val}${row.suffix}` : '-';
                  const isNeg = typeof val === 'string' && parseFloat(val) < 0;
                  return <DataCell key={fund.id} value={displayVal} color={val === null || val === '-' ? C.mutedLt : isNeg ? C.danger : C.text} mono />;
                })}
              </ColGrid>
            ))}
          </div>
        </>
      )}

      {/* Terms */}
      {activeSection === 'terms' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: `180px repeat(${funds.length}, 1fr)`, gap: '10px', marginBottom: '6px' }}>
            <div />
            {funds.map((f, i) => (
              <div key={f.id} style={{ fontSize: '10px', fontWeight: 700, color: COLORS[i], letterSpacing: '0.4px', textTransform: 'uppercase', paddingLeft: '14px' }}>{f.name}</div>
            ))}
          </div>
          {[
            { label: 'Management Fee',    key: 'management_fee',    format: (v: any) => v != null ? `${v}%` : '-' },
            { label: 'Performance Fee',   key: 'performance_fee',   format: (v: any) => v != null ? `${v}%` : '-' },
            { label: 'Min Subscription',  key: 'minimum_subscription', format: (v: any) => v ? `$${v}mm` : '-' },
            { label: 'Lockup',            key: 'lockup_months',     format: (v: any) => v ? `${v} months` : 'None' },
            { label: 'Redemption',        key: 'redemption_period', format: (v: any) => v || '-' },
            { label: 'Vehicle Type',      key: 'vehicle_type',      format: (v: any) => v || '-' },
            { label: 'Open to SMAs',      key: 'open_to_sma',       format: (v: any) => v ? 'Yes' : 'No' },
          ].map(row => (
            <ColGrid key={row.key}>
              <RowLabel label={row.label} />
              {funds.map((fund) => (
                <DataCell key={fund.id} value={row.format((fund as any)[row.key])} mono={['management_fee','performance_fee','minimum_subscription','lockup_months'].includes(row.key)} />
              ))}
            </ColGrid>
          ))}
        </div>
      )}

      {/* Overview */}
      {activeSection === 'overview' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: `180px repeat(${funds.length}, 1fr)`, gap: '10px', marginBottom: '6px' }}>
            <div />
            {funds.map((f, i) => (
              <div key={f.id} style={{ fontSize: '10px', fontWeight: 700, color: COLORS[i], letterSpacing: '0.4px', textTransform: 'uppercase', paddingLeft: '14px' }}>{f.name}</div>
            ))}
          </div>
          {[
            { label: 'Asset Class',    key: 'asset_class',    format: (v: any) => classLabels[v] || v || '-' },
            { label: 'Strategy',       key: 'strategy',       format: (v: any) => v || '-' },
            { label: 'Regional Focus', key: 'regional_focus', format: (v: any) => v || '-' },
            { label: 'Headquarters',   key: 'headquarters',   format: (v: any) => v || '-' },
            { label: 'AUM',            key: 'aum_min',        format: (_v: any, fund: Fund) => fund.aum_min ? `$${fund.aum_min}–${fund.aum_max}mm` : '-' },
            { label: 'Inception',      key: 'inception_date', format: (v: any) => v ? new Date(v).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '-' },
            { label: 'Firm',           key: 'firm',           format: (v: any) => v?.name || '-' },
          ].map(row => (
            <ColGrid key={row.key}>
              <RowLabel label={row.label} />
              {funds.map((fund) => (
                <DataCell key={fund.id} value={row.format((fund as any)[row.key], fund)} mono={row.key === 'aum_min'} />
              ))}
            </ColGrid>
          ))}
        </div>
      )}
    </div>
  );
}